import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight, Plus } from "lucide-react";
import Jar from "@/components/visual/Jar";
import { useNewsletter } from "@/components/marketing/NewsletterSection";
import { BRAND, LAB, PURITY_REPORT } from "@/content/brand";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

/**
 * "From the palm to your spoon": the story opens one chapter per tap, in
 * place, and ends at the choice to subscribe. Every chapter is in the HTML
 * from the start (closed ones are `hidden`), so search engines and screen
 * readers get the whole story; the tapping is only for people.
 */
const CHAPTERS = [
  {
    key: "palm",
    tag: "The palm",
    title: "It starts on two coconut lands we own.",
    body: `Most of our coconuts grow around ${BRAND.origins[0].name} in inland Karnataka. The rest grow on our coastal farms from Gokarna to Shirali, in sea air and monsoon rain. We do not buy oil and put our name on it.`,
    stat: ["2", "family farms in Karnataka"],
    link: ["/the-farm", "Visit the farms"],
  },
  {
    key: "copra",
    tag: "The copra",
    title: "We dry our own coconuts into copra.",
    body: "Copra is the dried kernel the oil is pressed from. Drying it ourselves means we know every coconut that goes into the press, which is the one thing a label cannot promise you.",
    stat: ["1", "ingredient, start to finish"],
    link: ["/cold-press-process", "How it is made"],
  },
  {
    key: "press",
    tag: "The press",
    title: "Pressed cold. Never refined.",
    body: "The copra is pressed with the temperature controlled so no heat is added, then the oil settles and is filtered. No bleaching, no deodorising, no solvent. That is why it still smells of coconut.",
    stat: ["0", "refining steps"],
    link: ["/cold-pressed-vs-refined-coconut-oil", "Cold pressed vs refined"],
  },
  {
    key: "lab",
    tag: "The lab",
    title: "Every batch waits for a lab result.",
    body: `A sample of every batch goes to an independent food testing lab. Our latest sample, tested at ${LAB.name}, ${LAB.city}, met the FSSAI standard for coconut oil on all ${PURITY_REPORT.rows.length} purity tests.`,
    stat: [BRAND.batchesTested, "batches tested so far"],
    link: ["/lab-reports", "Read the reports"],
  },
  {
    key: "jar",
    tag: "The jar",
    title: "A wide jar, because coconut oil sets.",
    body: "Below about 24°C pure coconut oil turns white and solid. In a narrow bottle that means a knife in the neck or a pan of hot water. In a wide jar it means a clean, dry spoon. Every jar carries a batch number you can quote to us for its report.",
    stat: ["24°C", "the point it turns solid"],
    link: ["/why-does-coconut-oil-turn-white", "Why it sets"],
    jar: true,
  },
  {
    key: "kitchen",
    tag: "Your kitchen",
    title: "One jar, many jobs.",
    body: "A teaspoon in the tadka, a few drops on the dosa tawa, the finishing spoon over avial. The same oil warms up for the Sunday champi and goes on dry heels after a bath.",
    stat: ["1 tsp", "is enough for a family tadka"],
    link: ["/recipes", "Cook with it"],
  },
];

function Chapter({ c, i, open, total, reduce }) {
  // Always rendered so the full story is in the page HTML; closed chapters are `hidden`.
  const Wrap = reduce ? "div" : motion.div;
  const anim = reduce ? {} : {
    initial: false,
    animate: open ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 },
    transition: { duration: 0.6, ease: [0.22, 0.61, 0.36, 1] },
  };
  return (
    <li hidden={!open} id={`chapter-${c.key}`} aria-label={`${i + 1} of ${total}: ${c.tag}`}>
      <Wrap {...anim} className="grid md:grid-cols-[1.35fr_1fr] gap-8 md:gap-14 items-center py-12 sm:py-16 border-t border-white/10">
        <div>
          <p className="font-body text-[13px] font-semibold tracking-[0.18em] uppercase text-[#F3E3A1]">
            {String(i + 1).padStart(2, "0")} · {c.tag}
          </p>
          <h3 tabIndex={-1} className="chapter-title mt-3 font-display text-white text-[clamp(1.9rem,4.4vw,3.2rem)] leading-[1.04] tracking-heading outline-none">
            {c.title}
          </h3>
          <p className="mt-5 max-w-xl font-body text-[17px] sm:text-[19px] leading-relaxed text-brand-bone/75">{c.body}</p>
          <Link to={c.link[0]} className="mt-6 inline-flex items-center gap-1 font-body text-[15px] text-white hover:underline">
            {c.link[1]} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {c.jar ? (
          <div className="mx-auto w-[200px] sm:w-[240px]"><Jar state="solid" className="w-full h-auto" /></div>
        ) : (
          <div className="rounded-tile bg-white/[0.04] ring-1 ring-white/10 p-8 sm:p-10">
            <p className="font-display text-white text-[clamp(3.5rem,9vw,6rem)] leading-none tracking-display">{c.stat[0]}</p>
            <p className="mt-3 font-body text-[16px] text-brand-bone/70">{c.stat[1]}</p>
          </div>
        )}
      </Wrap>
    </li>
  );
}

function SubscribeChoice({ reduce }) {
  const [email, setEmail] = useState("");
  const { subscribe, loading, modal } = useNewsletter();
  const Wrap = reduce ? "div" : motion.div;
  const anim = reduce ? {} : { initial: { opacity: 0, y: 28 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
  const onSubmit = async (e) => {
    e.preventDefault();
    const r = await subscribe(email, "story");
    if (r.ok) setEmail("");
  };
  return (
    <Wrap {...anim} className="mt-4 rounded-tile bg-[#F3E3A1] text-brand-ink p-5 sm:p-12" data-testid="story-subscribe">
      <p className="font-body text-[13px] font-semibold tracking-[0.18em] uppercase">That is the whole story</p>
      <h3 className="mt-3 font-display text-[clamp(1.9rem,4.4vw,3rem)] leading-[1.04] tracking-heading">Now keep it in your kitchen.</h3>
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="min-w-0 rounded-3xl bg-white/70 p-6 sm:p-8 flex flex-col">
          <p className="font-display text-[22px]">Never run out</p>
          <p className="mt-2 font-body text-[15px] leading-relaxed text-brand-ink/75">Pick a monthly refill reminder at checkout. We message you when your jar should be running low. No auto debit, you pay only when you reorder.</p>
          <Link to="/monthly-refill" className="mt-auto pt-6 inline-flex items-center gap-1 font-body text-[15px] font-medium hover:underline" data-testid="story-refill">
            How the monthly refill works <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <form onSubmit={onSubmit} className="min-w-0 rounded-3xl bg-brand-ink text-white p-6 sm:p-8 flex flex-col" data-testid="story-newsletter">
          <p className="font-display text-[22px]">Get the harvest letter</p>
          <p className="mt-2 font-body text-[15px] leading-relaxed text-white/70">Once a month: recipes, farm notes and the next batch report. Join now for 10% off your first order.</p>
          <div className="mt-auto pt-6 flex gap-2">
            <label htmlFor="story-email" className="sr-only">Email</label>
            <input id="story-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="h-12 min-w-0 flex-1 rounded-control bg-white/10 border border-white/20 px-5 font-body text-[15px] text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#F3E3A1]" />
            <button type="submit" disabled={loading} className="h-12 shrink-0 rounded-control bg-[#F3E3A1] px-5 font-body text-[15px] font-medium text-brand-ink disabled:opacity-60">
              {loading ? "Joining" : "Join"}
            </button>
          </div>
        </form>
      </div>
      {modal}
    </Wrap>
  );
}

export default function StoryExplorer() {
  const reduce = useReducedMotion() || (typeof window !== "undefined" && window.__PRERENDER__);
  const [opened, setOpened] = useState(1);
  const listRef = useRef(null);
  const total = CHAPTERS.length;
  const done = opened >= total;

  // Move focus to the chapter that just opened so keyboard and screen reader users follow along.
  useEffect(() => {
    if (opened <= 1) return;
    const el = listRef.current?.querySelector(`#chapter-${CHAPTERS[opened - 1].key} .chapter-title`);
    el?.focus({ preventScroll: true });
    el?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
  }, [opened, reduce]);

  const next = () => {
    const n = Math.min(total, opened + 1);
    setOpened(n);
    track("story_chapter_open", { chapter: CHAPTERS[n - 1].key, index: n });
  };

  return (
    <section className="bg-brand-obsidian text-brand-bone py-24 sm:py-32" aria-labelledby="story-heading" data-testid="home-story">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="font-body text-[15px] font-semibold text-brand-bone/70">From the palm to your spoon</p>
          <h2 id="story-heading" className="font-display text-white mt-2 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] tracking-display">
            Six steps. Nothing hidden.
          </h2>
          <p className="mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed text-brand-bone/70">
            Most coconut oil cannot tell you where it came from. Ours can, one step at a time.
          </p>
        </div>

        <ol ref={listRef} className="mt-10 list-none p-0">
          {CHAPTERS.map((c, i) => <Chapter key={c.key} c={c} i={i} open={i < opened} total={total} reduce={reduce} />)}
        </ol>

        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-4" aria-live="polite">
          {!done ? (
            <>
              <button type="button" onClick={next} aria-controls={`chapter-${CHAPTERS[opened].key}`}
                className="group inline-flex items-center gap-3 rounded-control bg-white text-brand-ink pl-2 pr-6 py-2 font-body text-[16px] font-medium hover:bg-[#F3E3A1] transition-colors"
                data-testid="story-next">
                <span className="w-9 h-9 rounded-full bg-brand-ink text-white flex items-center justify-center transition-transform group-hover:rotate-90"><Plus className="w-4 h-4" /></span>
                See more: {CHAPTERS[opened].tag.toLowerCase()}
              </button>
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5" aria-hidden="true">
                  {CHAPTERS.map((c, i) => <span key={c.key} className={cn("h-1.5 rounded-full transition-all duration-500", i < opened ? "w-6 bg-[#F3E3A1]" : "w-1.5 bg-white/25")} />)}
                </div>
                <span className="font-body text-[14px] text-brand-bone/60">{opened} of {total} opened</span>
              </div>
              <button type="button" onClick={() => setOpened(total)} className="font-body text-[14px] text-brand-bone/60 underline underline-offset-4 hover:text-white" data-testid="story-all">
                Show everything
              </button>
            </>
          ) : null}
        </div>
        {done && <SubscribeChoice reduce={reduce} />}
      </div>
    </section>
  );
}
